<script setup>
import { ref, computed, watch, markRaw } from 'vue';
import { useI18n } from 'vue-i18n';
import { VueFlow, useVueFlow } from '@vue-flow/core';
import { Background } from '@vue-flow/background';
import { Controls } from '@vue-flow/controls';
import '@vue-flow/core/dist/style.css';
import '@vue-flow/controls/dist/style.css';
import FlowStepNode from './FlowStepNode.vue';
import FlowEditPanel from './FlowEditPanel.vue';
import { useFlowBuilder } from './useFlowBuilder';

const props = defineProps({
  flowId: { type: Number, default: null },
});

const { t } = useI18n();

const nodeTypes = markRaw({ flowStep: FlowStepNode });

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'ar', label: 'Arabic' },
  { code: 'es', label: 'Spanish' },
  { code: 'fr', label: 'French' },
  { code: 'ur', label: 'Urdu' },
  { code: 'pt', label: 'Portuguese' },
  { code: 'sw', label: 'Swahili' },
  { code: 'hi', label: 'Hindi' },
  { code: 'zh', label: 'Mandarin' },
  { code: 'ru', label: 'Russian' },
];

const CATEGORIES = [
  { code: 'evangelism', label: 'Evangelism' },
  { code: 'discipleship', label: 'Discipleship' },
  { code: 'seeker', label: 'Seeker' },
  { code: 'follow-up', label: 'Follow-up' },
  { code: 'prayer', label: 'Prayer' },
];

const {
  flow,
  isSaving,
  isLoading,
  stepCount,
  addStep,
  removeStep,
  addOption,
  removeOption,
  save,
} = useFlowBuilder(props.flowId);

// ── Graph state ─────────────────────────────────────────────────────────────

const nodes = ref([]);
const edges = ref([]);
const selectedStepIndex = ref(null);

const COLS = 4;
const COL_W = 290;
const ROW_H = 230;

function positionKey() {
  return `vf-positions-${props.flowId ?? flow.value.id ?? 'new'}`;
}

function loadPositions() {
  try {
    return JSON.parse(localStorage.getItem(positionKey()) || '{}');
  } catch {
    return {};
  }
}

function persistPosition(nId, pos) {
  try {
    const stored = JSON.parse(localStorage.getItem(positionKey()) || '{}');
    stored[nId] = pos;
    localStorage.setItem(positionKey(), JSON.stringify(stored));
  } catch {
    // ignore
  }
}

function nodeId(step) {
  return `step-${step.id ?? step.tempKey}`;
}

function defaultPosition(i) {
  return { x: (i % COLS) * COL_W, y: Math.floor(i / COLS) * ROW_H };
}

function buildNodes(steps) {
  const positions = loadPositions();
  return steps.map((step, i) => {
    const id = nodeId(step);
    return {
      id,
      type: 'flowStep',
      position: positions[id] || defaultPosition(i),
      data: {
        step,
        isSelected: selectedStepIndex.value === i,
        onSelect: () => {
          selectedStepIndex.value = i;
        },
      },
    };
  });
}

function buildEdges(steps) {
  const result = [];
  steps.forEach(step => {
    step.options.forEach(opt => {
      if (!opt.next_step_id) return;
      const target = steps.find(s => (s.id ?? s.tempKey) === opt.next_step_id);
      if (!target) return;
      const handleId = `opt-${opt.id ?? opt.option_number}`;
      result.push({
        id: `${nodeId(step)}-${handleId}`,
        source: nodeId(step),
        sourceHandle: handleId,
        target: nodeId(target),
        label: opt.option_text || `Option ${opt.option_number}`,
        labelStyle: { fontSize: 10, fill: '#64748b' },
        labelBgStyle: { fill: '#f8fafc', fillOpacity: 0.9 },
        style: { stroke: '#1B2A4A', strokeWidth: 1.5 },
      });
    });
  });
  return result;
}

function syncGraph() {
  nodes.value = buildNodes(flow.value.steps);
  edges.value = buildEdges(flow.value.steps);
}

// Sync when steps are added/removed
watch(
  () => flow.value.steps.map(s => `${s.id ?? s.tempKey}`).join(','),
  syncGraph
);

// Sync edges when routing changes
watch(
  () =>
    flow.value.steps
      .flatMap(s =>
        s.options.map(o => `${o.id ?? o.option_number}:${o.next_step_id}`)
      )
      .join(','),
  () => {
    edges.value = buildEdges(flow.value.steps);
  }
);

// Refresh isSelected flag on nodes when selection changes
watch(selectedStepIndex, idx => {
  nodes.value = nodes.value.map((n, i) => ({
    ...n,
    data: { ...n.data, isSelected: i === idx },
  }));
});

// ── Vue Flow event hooks ─────────────────────────────────────────────────────

const { onConnect, onEdgesChange, onNodeDragStop } = useVueFlow();

onConnect(params => {
  const srcStep = flow.value.steps.find(s => nodeId(s) === params.source);
  const dstStep = flow.value.steps.find(s => nodeId(s) === params.target);
  if (!srcStep || !dstStep) return;

  const opt = srcStep.options.find(
    o => `opt-${o.id ?? o.option_number}` === params.sourceHandle
  );

  if (opt) {
    // Update existing option's target
    opt.next_step_id = dstStep.id ?? dstStep.tempKey;
  } else if (params.sourceHandle === 'default-out') {
    // No-option step: create a default option pointing to the target
    srcStep.options.push({
      option_number: 1,
      option_text: 'Continue',
      next_step_id: dstStep.id ?? dstStep.tempKey,
      tag_to_apply: '',
      notify_agent: false,
      outcome_label: '',
    });
  }
});

onEdgesChange(changes => {
  changes
    .filter(c => c.type === 'remove')
    .forEach(c => {
      const edge = edges.value.find(e => e.id === c.id);
      if (!edge) return;
      const srcStep = flow.value.steps.find(s => nodeId(s) === edge.source);
      if (!srcStep) return;
      const opt = srcStep.options.find(
        o => `opt-${o.id ?? o.option_number}` === edge.sourceHandle
      );
      if (opt) opt.next_step_id = null;
    });
});

onNodeDragStop(({ node }) => {
  persistPosition(node.id, node.position);
});

// ── Actions ──────────────────────────────────────────────────────────────────

const selectedStep = computed(() =>
  selectedStepIndex.value !== null
    ? flow.value.steps[selectedStepIndex.value]
    : null
);

function handleAddStep() {
  addStep();
  // watch on steps array will call syncGraph
}

function handleRemoveStep() {
  if (selectedStepIndex.value === null) return;
  removeStep(selectedStepIndex.value);
  selectedStepIndex.value = null;
}

function handleAddOption() {
  if (selectedStepIndex.value === null) return;
  addOption(selectedStepIndex.value);
}

function handleRemoveOption(optIndex) {
  if (selectedStepIndex.value === null) return;
  removeOption(selectedStepIndex.value, optIndex);
}

async function handleSave() {
  await save();
  syncGraph(); // re-sync to pick up real IDs after save
}
</script>

<template>
  <div class="flex flex-col h-full overflow-hidden bg-n-slate-1">
    <!-- Loading -->
    <div v-if="isLoading" class="flex items-center justify-center flex-1">
      <span class="i-lucide-loader-2 size-8 animate-spin text-woot-500" />
    </div>

    <template v-else>
      <!-- ── Header ── -->
      <div
        class="shrink-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center gap-4 shadow-sm"
      >
        <div class="flex-1 min-w-0">
          <input
            v-model="flow.name"
            type="text"
            :placeholder="t('MINISTRY.FLOW_BUILDER.NAME_PLACEHOLDER')"
            class="w-full text-xl font-semibold outline-none bg-transparent text-gray-900 placeholder-gray-300"
          />
          <input
            v-model="flow.description"
            type="text"
            :placeholder="t('MINISTRY.FLOW_BUILDER.DESCRIPTION_PLACEHOLDER')"
            class="w-full text-sm outline-none bg-transparent text-gray-500 placeholder-gray-300 mt-0.5"
          />
        </div>

        <div class="flex items-center gap-3 shrink-0">
          <select
            v-model="flow.language"
            class="text-sm px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-700 focus:outline-none focus:border-woot-400"
          >
            <option v-for="l in LANGUAGES" :key="l.code" :value="l.code">
              {{ l.label }}
            </option>
          </select>

          <select
            v-model="flow.category"
            class="text-sm px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-700 focus:outline-none focus:border-woot-400"
          >
            <option v-for="c in CATEGORIES" :key="c.code" :value="c.code">
              {{ c.label }}
            </option>
          </select>

          <label
            class="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none"
          >
            <span class="relative inline-flex">
              <input
                v-model="flow.is_active"
                type="checkbox"
                class="sr-only peer"
              />
              <div
                class="w-9 h-5 rounded-full bg-gray-300 peer-checked:bg-woot-500 transition-colors"
              />
              <div
                class="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-4"
              />
            </span>
            {{ t('MINISTRY.FLOW_BUILDER.ACTIVE') }}
          </label>

          <button
            class="flex items-center gap-2 px-4 py-2 rounded-lg bg-woot-500 text-white text-sm font-medium hover:bg-woot-600 transition-colors disabled:opacity-50"
            :disabled="isSaving || !flow.name"
            @click="handleSave"
          >
            <span
              v-if="isSaving"
              class="i-lucide-loader-2 size-4 animate-spin"
            />
            <span v-else class="i-lucide-save size-4" />
            {{
              isSaving
                ? t('MINISTRY.FLOW_BUILDER.SAVING')
                : t('MINISTRY.FLOW_BUILDER.SAVE_FLOW')
            }}
          </button>
        </div>
      </div>

      <!-- ── Canvas + Edit Panel ── -->
      <div class="flex flex-1 min-h-0 overflow-hidden">
        <!-- Vue Flow canvas -->
        <div class="flex-1 relative min-w-0">
          <!-- Empty state -->
          <div
            v-if="stepCount === 0"
            class="absolute inset-0 flex flex-col items-center justify-center z-10 pointer-events-none"
          >
            <span class="i-lucide-workflow size-16 text-gray-300 block mb-4" />
            <p class="text-lg font-semibold text-gray-700 mb-1">
              {{ t('MINISTRY.FLOW_BUILDER.EMPTY_TITLE') }}
            </p>
            <p class="text-gray-400 mb-6 text-sm">
              {{ t('MINISTRY.FLOW_BUILDER.EMPTY_HINT') }}
            </p>
          </div>

          <VueFlow
            v-model:nodes="nodes"
            v-model:edges="edges"
            :node-types="nodeTypes"
            fit-view-on-init
            :default-zoom="0.75"
            :min-zoom="0.2"
            :max-zoom="2"
            edges-updatable
            delete-key-code="Delete"
            class="w-full h-full"
          >
            <Background pattern-color="#e2e8f0" :gap="24" />
            <Controls />
          </VueFlow>

          <!-- Floating add-step button -->
          <button
            class="absolute bottom-6 left-6 z-10 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-woot-500 text-white text-sm font-medium shadow-lg hover:bg-woot-600 transition-colors"
            @click="handleAddStep"
          >
            <span class="i-lucide-plus size-4" />
            {{ t('MINISTRY.FLOW_BUILDER.ADD_STEP') }}
          </button>

          <!-- Hint when step is not selected -->
          <div
            v-if="stepCount > 0 && !selectedStep"
            class="absolute bottom-6 right-6 z-10 bg-white/90 border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-400 shadow-sm pointer-events-none"
          >
            {{ t('MINISTRY.FLOW_BUILDER.CANVAS_HINT') }}
          </div>
        </div>

        <!-- Edit panel (right sidebar) -->
        <FlowEditPanel
          v-if="selectedStep"
          :flow-steps="flow.steps"
          :step-index="selectedStepIndex"
          @close="selectedStepIndex = null"
          @remove="handleRemoveStep"
          @add-option="handleAddOption"
          @remove-option="handleRemoveOption"
        />
      </div>
    </template>
  </div>
</template>
